import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { type Disposable, noop, type Unsubscribe } from '@amazon/vinyl-util'
import type { Player } from '../core/api'

/**
 * Sleep-timer presets. Minute presets tick a countdown; end-of-track
 * subscribes to the next `trackEnded` event and pauses. Mirrors GH's set
 * (5/15/30/45/60 minutes + end-of-track).
 */
export type SleepTimerPreset =
    | 'endOfTrack'
    | 'minutes5'
    | 'minutes15'
    | 'minutes30'
    | 'minutes45'
    | 'minutes60'

const PRESET_MINUTES: Record<Exclude<SleepTimerPreset, 'endOfTrack'>, number> = {
    minutes5: 5,
    minutes15: 15,
    minutes30: 30,
    minutes45: 45,
    minutes60: 60,
}

export const SLEEP_TIMER_PRESETS: readonly SleepTimerPreset[] = [
    'endOfTrack',
    ...(Object.keys(PRESET_MINUTES) as SleepTimerPreset[]),
]

/** The countdown a preset starts from; null for end-of-track, which counts nothing down. */
export function sleepTimerPresetSeconds(preset: SleepTimerPreset): number | null {
    return preset === 'endOfTrack' ? null : PRESET_MINUTES[preset] * 60
}

export interface SleepTimer {
    /**
     * Remaining seconds on the active minute-based timer. `null` when no countdown is running
     * (i.e. inactive, or end-of-track waiting). UI renders a `mm:ss` label when non-null.
     */
    readonly remaining$: ObservableValue<number | null>
    /** True while any sleep timer is armed (counting down or waiting for `trackEnded`). */
    readonly active$: ObservableValue<boolean>
    /** Which preset is armed, or `null` when inactive; the preset menu marks its row. */
    readonly preset$: ObservableValue<SleepTimerPreset | null>
    /**
     * Arms the timer for `preset`, canceling any armed one first: the presets are one-of, not
     * additive. Minute presets tick every second and pause playback at zero; end-of-track
     * pauses on the next `trackEnded`, so the current track finishes but the next doesn't play.
     */
    start(preset: SleepTimerPreset): void
    /** Disarms without pausing. */
    cancel(): void
}

export class SleepTimerController implements SleepTimer, Disposable {
    private readonly cells = {
        remaining$: data<number | null>(null),
        active$: data(false),
        preset$: data<SleepTimerPreset | null>(null),
    }
    readonly remaining$: ObservableValue<number | null> = this.cells.remaining$
    readonly active$: ObservableValue<boolean> = this.cells.active$
    readonly preset$: ObservableValue<SleepTimerPreset | null> = this.cells.preset$
    private intervalId: ReturnType<typeof setInterval> | null = null
    private endedUnsub: Unsubscribe | null = null
    // Bumped by every cancel/start so a deferred `then()` from a superseded call can tell it's
    // stale; comparing presets isn't enough when a later call re-arms the same one.
    private generation = 0

    constructor(private readonly player: () => Promise<Player> | null) {}

    cancel(): void {
        this.generation++
        this.clearHandles()
        this.reset()
    }

    start(preset: SleepTimerPreset): void {
        this.cancel()
        const { remaining$, active$, preset$ } = this.cells
        active$.value = true
        preset$.value = preset
        const seconds = sleepTimerPresetSeconds(preset)
        if (seconds === null) {
            this.pauseAtTrackEnd()
            return
        }
        remaining$.value = seconds
        this.intervalId = setInterval(() => {
            const remaining = (remaining$.value ?? 0) - 1
            if (remaining > 0) {
                remaining$.value = remaining
                return
            }
            this.clearHandles()
            this.reset()
            this.player()
                ?.then((player) => player.pause())
                .catch(noop)
        }, 1000)
    }

    dispose(): void {
        this.clearHandles()
    }

    private pauseAtTrackEnd(): void {
        const generation = this.generation
        this.player()
            ?.then((player) => {
                if (generation !== this.generation) return
                const finish = () => {
                    player.pause()
                    this.endedUnsub = null
                    this.reset()
                }
                // The track already ended before the player resolved, so no `trackEnded` is coming.
                if (player.ended$.value) {
                    finish()
                    return
                }
                this.endedUnsub = player.on('trackEnded', finish, { once: true })
            })
            .catch(noop)
    }

    private reset(): void {
        this.cells.remaining$.value = null
        this.cells.active$.value = false
        this.cells.preset$.value = null
    }

    private clearHandles(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId)
            this.intervalId = null
        }
        this.endedUnsub?.()
        this.endedUnsub = null
    }
}
